import { Component } from '@theme/component';
import {
  center,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const SLIDE_VIDEO_SELECTOR = 'video.slide__video';
const SLIDE_VIDEO_AUDIO_ENABLED_ATTRIBUTE = 'data_slide_audio_enabled';
const SLIDE_VIDEO_AUTOPLAY_ENABLED_ATTRIBUTE = 'data_slide_autoplay_enabled';
const SLIDE_VIDEO_TOGGLE_SELECTOR = '[data-slide-video-toggle]';
const SLIDE_VIDEO_TOGGLE_PLAY_SELECTOR = '[data-slide-video-toggle-play]';
const SLIDE_VIDEO_TOGGLE_PAUSE_SELECTOR = '[data-slide-video-toggle-pause]';
const SLIDE_VIDEO_DEFERRED_ATTRIBUTE = 'data-slide-video-load-deferred';

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#syncRafId !== null) {
      cancelAnimationFrame(this.#syncRafId);
      this.#syncRafId = null;
    }

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    if (this.#slideVideoMediaListenersAttached) {
      this.removeEventListener('playing', this.#onSlideVideoMediaPlaying, true);
      this.removeEventListener('pause', this.#onSlideVideoMediaPause, true);
      this.#slideVideoMediaListenersAttached = false;
    }

    this.#offsetSyncStyleOk = null;
    this.#slideVideosReadyAfterPaint = false;
    this.#initialSlideVideoSyncPending = false;
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );

    // After any user-initiated selection (dots, arrows), pause autoplay for the rest of the session
    if (event != null) {
      this.#userPausedAutoplay = true;
      this.pause();
    }
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   * Does not resume if the user has manually interacted with the slideshow (dots, arrows, or drag);
   * autoplay then resumes only after a full page reload.
   */
  resume() {
    if (this.#userPausedAutoplay || !this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    if (this.hasAttribute('carousel-dots') && dots?.length && this.#carouselVisibleCount > 0) {
      const pageIndex = Math.min(
        Math.floor(value / this.#carouselVisibleCount),
        Math.ceil((slides?.length ?? 0) / this.#carouselVisibleCount) - 1
      );
      dots.forEach((el, i) => el.setAttribute('aria-selected', `${i === pageIndex}`));
    } else {
      for (const controls of [thumbnails, dots]) {
        controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
      }
    }

    const allSlidesVisible = slides && slides.length > 0 && this.visibleSlides.length >= slides.length;
    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(allSlidesVisible || (!this.infinite && slides && this.nextIndex >= slides.length));

    const slideshowArrows = (previous ?? next)?.closest('slideshow-arrows');
    if (slideshowArrows instanceof HTMLElement) {
      slideshowArrows.hidden = Boolean(previous?.disabled && next?.disabled);
    }

    this.#syncSlideVideos();
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * When carousel-dots: number of slides visible per "page" (used for page-based dot count and selection).
   * @type {number}
   */
  #carouselVisibleCount = 0;

  /**
   * Set to true when the user manually interacts (dots, arrows, or drag). Autoplay then stays paused
   * until page reload.
   * @type {boolean}
   */
  #userPausedAutoplay = false;

  /**
   * Coalesces scroll-position sync to one requestAnimationFrame (reduces forced reflow churn).
   * @type {number | null}
   */
  #syncRafId = null;

  /**
   * Cached result of direction/writing-mode check for offset-based #sync (avoids getComputedStyle every scroll).
   * `null` = not yet validated or invalidated (e.g. resize).
   * @type {boolean | null}
   */
  #offsetSyncStyleOk = null;

  /**
   * Top-level hero slideshows defer autoplay video sync until after window load + paint so LCP can settle on the poster img.
   * @type {boolean}
   */
  #slideVideosReadyAfterPaint = false;

  /** @type {boolean} */
  #initialSlideVideoSyncPending = false;

  /** @type {boolean} */
  #slideVideoMediaListenersAttached = false;

  /** @param {Event} event */
  #onSlideVideoMediaPlaying = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLVideoElement)) return;
    if (!target.matches(SLIDE_VIDEO_SELECTOR)) return;
    target.closest('.slide__image-container')?.classList.add('slide__image-container--video-playing');
  };

  /** @param {Event} event */
  #onSlideVideoMediaPause = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLVideoElement)) return;
    if (!target.matches(SLIDE_VIDEO_SELECTOR)) return;
    target.closest('.slide__image-container')?.classList.remove('slide__image-container--video-playing');
  };

  #attachSlideVideoMediaListeners() {
    if (this.#slideVideoMediaListenersAttached) return;
    this.#slideVideoMediaListenersAttached = true;
    this.addEventListener('playing', this.#onSlideVideoMediaPlaying, true);
    this.addEventListener('pause', this.#onSlideVideoMediaPause, true);
  }

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.#attachSlideVideoMediaListeners();
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }

    const { slideshowContainer } = this.refs;
    if (slideshowContainer instanceof HTMLElement) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#syncSlideVideos();
      });
      this.#resizeObserver.observe(slideshowContainer);
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    this.#attachSlideVideoMediaListeners();
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
        this.#updateCarouselDots();
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        this.#offsetSyncStyleOk = null;

        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }

        this.#updateCarouselDots();
        this.#syncSlideVideos();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.#syncRafId !== null) return;

    this.#syncRafId = requestAnimationFrame(() => {
      this.#syncRafId = null;
      if (!this.isConnected) return;

      const previousIndex = this.#current;
      const index = this.#sync();

      if (index === previousIndex) return;

      const slide = this.slides?.[index];
      if (!slide) return;

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: true,
          trigger: 'scroll',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
    });
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * When true, #sync can use offsetLeft/Top + one scroller rect instead of N× getBoundingClientRect.
   * Skips RTL and non-horizontal writing modes where scroll/offset math is unreliable.
   *
   * @param {HTMLElement} scroller
   * @param {HTMLElement[]} visibleSlides
   */
  #canSyncSlideshowWithOffsets(scroller, visibleSlides) {
    for (const slide of visibleSlides) {
      if (slide.offsetParent !== scroller) {
        this.#offsetSyncStyleOk = null;
        return false;
      }
    }

    if (this.#offsetSyncStyleOk === true) return true;
    if (this.#offsetSyncStyleOk === false) return false;

    const style = getComputedStyle(scroller);
    if (style.direction === 'rtl') {
      this.#offsetSyncStyleOk = false;
      return false;
    }

    const wm = style.writingMode || 'horizontal-tb';
    const ok = wm === 'horizontal-tb' || wm === 'lr-tb' || wm === 'rl-tb';
    this.#offsetSyncStyleOk = ok;
    return ok;
  }

  /**
   * Picks the visible slide whose center is closest to the reference point (same semantics as the legacy path).
   *
   * @param {HTMLElement[]} slides
   * @param {HTMLElement[]} visibleSlides
   * @param {'x' | 'y'} axis
   * @param {HTMLElement} scroller
   * @returns {number} Index into `slides`, or 0 if not found.
   */
  #syncSlideIndexFromOffsets(slides, visibleSlides, axis, scroller) {
    const scrollerRect = scroller.getBoundingClientRect();
    const multi = visibleSlides.length > 1;

    const first = visibleSlides[0];
    if (!first) return 0;

    let bestSlide = first;
    let bestDist = Infinity;

    if (axis === 'x') {
      const referencePoint = multi ? scrollerRect.x : scrollerRect.left + scrollerRect.width / 2;
      const scrollL = scroller.scrollLeft;
      const originX = scrollerRect.left;

      for (let i = 0; i < visibleSlides.length; i++) {
        const slide = visibleSlides[i];
        if (!slide) continue;
        const centerViewport = originX + slide.offsetLeft - scrollL + slide.offsetWidth / 2;
        const d = Math.abs(centerViewport - referencePoint);
        if (d < bestDist) {
          bestDist = d;
          bestSlide = slide;
        }
      }
    } else {
      const referencePoint = multi ? scrollerRect.y : scrollerRect.top + scrollerRect.height / 2;
      const scrollT = scroller.scrollTop;
      const originY = scrollerRect.top;

      for (let i = 0; i < visibleSlides.length; i++) {
        const slide = visibleSlides[i];
        if (!slide) continue;
        const centerViewport = originY + slide.offsetTop - scrollT + slide.offsetHeight / 2;
        const d = Math.abs(centerViewport - referencePoint);
        if (d < bestDist) {
          bestDist = d;
          bestSlide = slide;
        }
      }
    }

    const index = slides.indexOf(bestSlide);
    return index >= 0 ? index : 0;
  }

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) {
      if (this.#current !== 0) this.current = 0;
      return this.#current;
    }

    if (!this.#scroll) {
      if (this.#current !== 0) this.current = 0;
      return this.#current;
    }

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.#current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;

    let index;

    if (this.#canSyncSlideshowWithOffsets(scroller, visibleSlides)) {
      index = this.#syncSlideIndexFromOffsets(slides, visibleSlides, axis, scroller);
    } else {
      const referencePoint =
        visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);

      /** @type {HTMLElement | null} */
      let bestSlide = null;
      let bestDist = Infinity;

      for (const slide of visibleSlides) {
        const c = center(slide, axis);
        const d = Math.abs(c - referencePoint);
        if (d < bestDist) {
          bestDist = d;
          bestSlide = slide;
        }
      }

      if (!bestSlide) {
        if (this.#current !== 0) this.current = 0;
        return this.#current;
      }

      index = slides.indexOf(bestSlide);
      if (index < 0) index = 0;
    }

    if (index === this.#current) return this.#current;
    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.#userPausedAutoplay = true;
        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      // Do not resume autoplay after user drag; it resumes only after page reload
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * When carousel-dots: show one dot per "page" (visible group), wire dot clicks to select that page's first slide.
   */
  #updateCarouselDots() {
    if (!this.hasAttribute('carousel-dots')) return;

    const { scroller, dots, slides, slideshowControls } = this.refs;
    if (!scroller || !dots?.length || !slides?.length) return;

    const firstSlide = slides[0];
    if (!firstSlide) return;

    const scrollerStyles = getComputedStyle(scroller);
    const gapValue =
      scrollerStyles.getPropertyValue('--slideshow-gap').trim() || scrollerStyles.gap || '0';
    const gap = parseFloat(gapValue) || 0;
    const slideWidth = firstSlide.getBoundingClientRect().width;
    if (slideWidth <= 0) return;

    const visibleCount = Math.max(
      1,
      Math.round((scroller.offsetWidth + gap) / (slideWidth + gap))
    );
    const pageCount = Math.ceil(slides.length / visibleCount);

    this.#carouselVisibleCount = visibleCount;

    // Hide/show the parent <li> of each dot (slideshow-controls__dots li) instead of the whole controls
    const dotLi = (/** @type {HTMLElement} */ el) => el?.parentElement;

    dots.forEach((dot, i) => {
      const li = dotLi(dot);
      if (i >= pageCount || pageCount <= 1) {
        if (li) li.hidden = true;
        return;
      }
      if (li) li.hidden = false;
      dot.setAttribute('on:click', `/select/${i * visibleCount}`);
    });

    // Hide slideshow-controls when all slides fit (no next slides to navigate to)
    const anyDotVisible = pageCount > 1;
    if (slideshowControls instanceof HTMLElement) {
      slideshowControls.hidden = !anyDotVisible;
    }

    // Re-apply current page selection
    this.current = this.current;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Keeps slideshow video playback in sync with the currently active slide.
   * Only the active slide's visible video can play audio when the slide opts in.
   */
  #syncSlideVideos() {
    const allSlides = this.refs.slides;
    if (!allSlides?.length) return;

    if (!this.isNested && this.querySelector(SLIDE_VIDEO_SELECTOR)) {
      if (!this.#slideVideosReadyAfterPaint) {
        if (!this.#initialSlideVideoSyncPending) {
          this.#initialSlideVideoSyncPending = true;
          const runDeferredSync = () => {
            this.#initialSlideVideoSyncPending = false;
            this.#slideVideosReadyAfterPaint = true;
            this.#syncSlideVideos();
          };
          if (document.readyState === 'complete') {
            requestAnimationFrame(() => requestAnimationFrame(runDeferredSync));
          } else {
            window.addEventListener(
              'load',
              () => requestAnimationFrame(() => requestAnimationFrame(runDeferredSync)),
              { once: true }
            );
          }
        }
        return;
      }
    }

    const activeSlide = this.slides?.[this.#current] ?? allSlides[this.#current] ?? allSlides[0];
    if (!activeSlide) return;

    allSlides.forEach((slide) => {
      const slideVideos = /** @type {NodeListOf<HTMLVideoElement>} */ (slide.querySelectorAll(SLIDE_VIDEO_SELECTOR));
      if (!slideVideos.length) return;

      const isActiveSlide = slide === activeSlide;
      const visibleSlideVideo = isActiveSlide ? this.#getVisibleSlideVideo(slideVideos) : null;
      const toggleButton = slide.querySelector(SLIDE_VIDEO_TOGGLE_SELECTOR);

      slideVideos.forEach((video) => {
        if (video !== visibleSlideVideo) {
          this.#pauseAndMuteVideo(video);
          return;
        }

        const audioEnabled = video.getAttribute(SLIDE_VIDEO_AUDIO_ENABLED_ATTRIBUTE) === 'true';
        const autoplayEnabled = video.getAttribute(SLIDE_VIDEO_AUTOPLAY_ENABLED_ATTRIBUTE) !== 'false';

        if (!autoplayEnabled) {
          // With autoplay disabled, keep playback user-driven via native controls.
          video.muted = !audioEnabled;
          return;
        }

        this.#playVideoWithFallback(video, { withAudio: audioEnabled });
      });

      if (toggleButton instanceof HTMLButtonElement) {
        const showToggle = isActiveSlide && visibleSlideVideo != null;
        toggleButton.hidden = !showToggle;
        this.#setSlideVideoToggleState(toggleButton, Boolean(showToggle && visibleSlideVideo && !visibleSlideVideo.paused));
      }
    });
  }

  /**
   * Handles play/pause toggle clicks for slides where autoplay is disabled.
   * Triggered by on:click="/toggleSlideVideo" on the toggle button.
   * @param {MouseEvent} event
   */
  async toggleSlideVideo(event) {
    if (!(event.target instanceof Element)) return;

    const toggleButton = event.target.closest(SLIDE_VIDEO_TOGGLE_SELECTOR);
    if (!(toggleButton instanceof HTMLButtonElement)) return;

    const slide = toggleButton.closest('slideshow-slide');
    if (!(slide instanceof HTMLElement)) return;

    event.preventDefault();

    const slideVideos = /** @type {NodeListOf<HTMLVideoElement>} */ (slide.querySelectorAll(SLIDE_VIDEO_SELECTOR));
    const visibleSlideVideo = this.#getVisibleSlideVideo(slideVideos);
    if (!visibleSlideVideo) return;

    if (visibleSlideVideo.paused) {
      this.#pauseControlledSlideVideosExcept(visibleSlideVideo);
      const audioEnabled = visibleSlideVideo.getAttribute(SLIDE_VIDEO_AUDIO_ENABLED_ATTRIBUTE) === 'true';
      const isPlaying = await this.#playVideoWithFallback(visibleSlideVideo, { withAudio: audioEnabled });
      this.#setSlideVideoToggleState(toggleButton, isPlaying);
    } else {
      this.#pauseAndMuteVideo(visibleSlideVideo);
      this.#setSlideVideoToggleState(toggleButton, false);
    }
  }

  /**
   * Ensures only one manually-controlled slideshow video plays at a time.
   * Pauses every other slide video where autoplay is disabled.
   * @param {HTMLVideoElement} currentVideo
   */
  #pauseControlledSlideVideosExcept(currentVideo) {
    const manualVideoSelector = `${SLIDE_VIDEO_SELECTOR}[${SLIDE_VIDEO_AUTOPLAY_ENABLED_ATTRIBUTE}="false"]`;
    const manualVideos = document.querySelectorAll(manualVideoSelector);

    manualVideos.forEach((video) => {
      if (!(video instanceof HTMLVideoElement) || video === currentVideo) return;

      this.#pauseAndMuteVideo(video);

      const toggleButton = video.closest('slideshow-slide')?.querySelector(SLIDE_VIDEO_TOGGLE_SELECTOR);
      if (toggleButton instanceof HTMLButtonElement) {
        this.#setSlideVideoToggleState(toggleButton, false);
      }
    });
  }

  /**
   * Updates visual/aria state for a slide video toggle.
   * @param {HTMLButtonElement} toggleButton
   * @param {boolean} isPlaying
   */
  #setSlideVideoToggleState(toggleButton, isPlaying) {
    const playIcon = toggleButton.querySelector(SLIDE_VIDEO_TOGGLE_PLAY_SELECTOR);
    const pauseIcon = toggleButton.querySelector(SLIDE_VIDEO_TOGGLE_PAUSE_SELECTOR);

    if (playIcon instanceof HTMLElement) {
      playIcon.hidden = isPlaying;
    }

    if (pauseIcon instanceof HTMLElement) {
      pauseIcon.hidden = !isPlaying;
    }

    const playLabel = toggleButton.getAttribute('data-play-label') ?? 'Play video';
    const pauseLabel = toggleButton.getAttribute('data-pause-label') ?? 'Pause video';
    toggleButton.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
  }

  /**
   * Returns the currently visible slide video, accounting for desktop/mobile variants.
   * @param {NodeListOf<HTMLVideoElement>} videos
   * @returns {HTMLVideoElement | null}
   */
  #getVisibleSlideVideo(videos) {
    const visibleVideo = Array.from(videos).find((video) => {
      if (video.getClientRects().length === 0) return false;

      const { display, visibility } = getComputedStyle(video);
      return display !== 'none' && visibility !== 'hidden';
    });

    return visibleVideo ?? videos[0] ?? null;
  }

  /**
   * Pauses a video and ensures it does not output audio.
   * @param {HTMLVideoElement} video
   */
  #pauseAndMuteVideo(video) {
    video.muted = true;
    video.pause();
  }

  /**
   * Promotes deferred slide videos (preload=none) to full fetch before play().
   * @param {HTMLVideoElement} video
   */
  async #hydrateDeferredSlideVideo(video) {
    if (!video.hasAttribute(SLIDE_VIDEO_DEFERRED_ATTRIBUTE)) return;

    video.removeAttribute(SLIDE_VIDEO_DEFERRED_ATTRIBUTE);
    video.preload = 'auto';

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;

    video.load();

    await new Promise((resolve) => {
      /** @type {() => void} */
      const done = () => {
        video.removeEventListener('loadeddata', done);
        video.removeEventListener('error', done);
        resolve(undefined);
      };
      video.addEventListener('loadeddata', done, { once: true });
      video.addEventListener('error', done, { once: true });
    });
  }

  /**
   * Attempts to play a video with requested audio behavior, then gracefully falls back to muted playback.
   * @param {HTMLVideoElement} video
   * @param {{ withAudio: boolean }} options
   */
  async #playVideoWithFallback(video, { withAudio }) {
    await this.#hydrateDeferredSlideVideo(video);
    video.muted = !withAudio;
    try {
      await video.play();
      return true;
    } catch (error) {
      if (!withAudio) return false;

      // Browsers can block autoplay with sound; keep video moving muted when that happens.
      video.muted = true;

      try {
        await video.play();
        return true;
      } catch (fallbackError) {
        return false;
      }
    }
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
      // Refresh next/previous disabled state when visible slides change (e.g. all slides fit in view)
      this.current = this.#current;
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
